import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Trash2, X, Zap, Settings, Tag,
  ToggleLeft, ToggleRight, Target, Users,
  TrendingUp, Save
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ── EVENTOS (35) ─────────────────────────────────────────────── */
const EVENT_GROUPS = [
  {
    group: 'Vendedores & Compradores',
    icon: '👤',
    events: [
      { value: 'seller_added',           label: 'Se añade un nuevo vendedor' },
      { value: 'buyer_added',            label: 'Se añade un nuevo comprador' },
      { value: 'seller_completed',       label: 'Vendedor cierra operación' },
      { value: 'buyer_completed',        label: 'Comprador cierra operación' },
      { value: 'seller_status_changed',  label: 'Cambia el estado de un vendedor' },
      { value: 'buyer_status_changed',   label: 'Cambia el estado de un comprador' },
      { value: 'seller_lost',            label: 'Vendedor marcado como perdido' },
      { value: 'buyer_lost',             label: 'Comprador marcado como perdido' },
    ],
  },
  {
    group: 'Propiedades',
    icon: '🏠',
    events: [
      { value: 'property_crm_added',     label: 'Se capta una nueva propiedad' },
      { value: 'property_price_changed', label: 'Cambia el precio de una propiedad' },
      { value: 'exclusivity_signed',     label: 'Se firma acuerdo de exclusividad' },
      { value: 'commission_agreed',      label: 'Se acuerda porcentaje de comisión' },
      { value: 'property_visit_done',    label: 'Se realiza visita a la propiedad' },
      { value: 'property_offer_received',label: 'Se recibe oferta en una propiedad' },
    ],
  },
  {
    group: 'Profesionales',
    icon: '💼',
    events: [
      { value: 'professional_added',     label: 'Se añade un profesional al CRM' },
      { value: 'professional_contract',  label: 'Profesional firma contrato' },
    ],
  },
  {
    group: 'Tareas',
    icon: '✅',
    events: [
      { value: 'task_created',           label: 'Se crea una tarea nueva' },
      { value: 'task_completed',         label: 'Se completa una tarea' },
      { value: 'task_overdue',           label: 'Tarea vence sin completar' },
      { value: 'task_progress_50',       label: 'Tarea alcanza el 50% de progreso' },
      { value: 'task_progress_75',       label: 'Tarea alcanza el 75% de progreso' },
      { value: 'task_assigned',          label: 'Tarea asignada a un agente' },
    ],
  },
  {
    group: 'Equipo',
    icon: '👥',
    events: [
      { value: 'member_joined',          label: 'Nuevo miembro se une a la org' },
      { value: 'member_left',            label: 'Miembro abandona la organización' },
      { value: 'member_role_changed',    label: 'Cambia el rol de un miembro' },
      { value: 'member_inactive_days',   label: 'Agente sin actividad X días' },
    ],
  },
  {
    group: 'Objetivos',
    icon: '🎯',
    events: [
      { value: 'objective_contacts_reached',  label: 'Objetivo de contactos alcanzado' },
      { value: 'objective_sellers_reached',   label: 'Objetivo de vendedores alcanzado' },
      { value: 'objective_buyers_reached',    label: 'Objetivo de compradores alcanzado' },
      { value: 'objective_closings_reached',  label: 'Objetivo de cierres alcanzado' },
      { value: 'objective_commission_reached',label: 'Objetivo de comisiones alcanzado' },
    ],
  },
  {
    group: 'Inactividad & Seguimiento',
    icon: '⏰',
    events: [
      { value: 'inactivity_seller_days', label: 'Sin actividad en vendedor X días' },
      { value: 'inactivity_buyer_days',  label: 'Sin actividad en comprador X días' },
      { value: 'no_followup_first_contact', label: 'Primer contacto sin seguimiento X días' },
      { value: 'pipeline_stage_reached', label: 'Contacto alcanza cierta etapa del pipeline' },
    ],
  },
  {
    group: 'Resúmenes automáticos',
    icon: '📊',
    events: [
      { value: 'daily_summary',          label: 'Enviar resumen diario de actividad' },
      { value: 'weekly_summary',         label: 'Enviar resumen semanal de actividad' },
      { value: 'monthly_summary',        label: 'Enviar resumen mensual de resultados' },
    ],
  },
];

/* ── ACCIONES (9) ─────────────────────────────────────────────── */
const ACTION_GROUPS = [
  {
    group: 'Email',
    icon: '📧',
    actions: [
      { value: 'email_admin',       label: 'Email al administrador' },
      { value: 'email_agent',       label: 'Email al agente responsable' },
      { value: 'email_all_members', label: 'Email a todos los miembros' },
    ],
  },
  {
    group: 'Notificaciones',
    icon: '🔔',
    actions: [
      { value: 'notification_bell',     label: 'Notificación de campana (admin)' },
      { value: 'notification_bell_all', label: 'Notificación de campana (todos)' },
    ],
  },
  {
    group: 'Mensajes',
    icon: '💬',
    actions: [
      { value: 'org_group_message',     label: 'Mensaje en chat de equipo' },
      { value: 'direct_message_agent',  label: 'Mensaje directo al agente' },
    ],
  },
  {
    group: 'Tareas & Alertas',
    icon: '⚡',
    actions: [
      { value: 'create_task',  label: 'Crear tarea automáticamente' },
      { value: 'create_alert', label: 'Crear alerta en el sistema' },
    ],
  },
];

/* ── Flatten para lookups ────────────────────────────────────── */
const ALL_EVENTS = EVENT_GROUPS.flatMap(g => g.events);
const ALL_ACTIONS = ACTION_GROUPS.flatMap(g => g.actions);
const EVENT_LABEL = Object.fromEntries(ALL_EVENTS.map(e => [e.value, e.label]));
const ACTION_LABEL = Object.fromEntries(ALL_ACTIONS.map(a => [a.value, a.label]));

/* ── Helpers UI ───────────────────────────────────────────────── */
const inputCls = 'w-full bg-gray-800 border border-white/20 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-yellow-500';

const Section = ({ title, color, children }) => {
  const borders = { gold: 'border-yellow-500/30', blue: 'border-blue-500/30', teal: 'border-teal-500/30', orange: 'border-orange-500/30', green: 'border-green-500/30', yellow: 'border-yellow-500/30', pink: 'border-yellow-500/30' };
  const headers = { gold: 'text-yellow-400', blue: 'text-blue-400', teal: 'text-teal-400', orange: 'text-orange-400', green: 'text-green-400', yellow: 'text-yellow-400', pink: 'text-yellow-400' };
  return (
    <div className={`bg-gray-900 border-2 ${borders[color] || 'border-white/10'} rounded-xl p-5`}>
      <h3 className={`text-sm font-bold uppercase tracking-wide mb-4 ${headers[color] || 'text-gray-300'}`}>{title}</h3>
      {children}
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-400 mb-1.5">{label}</label>
    {children}
  </div>
);

/* ── Eventos con params extra ─────────────────────────────────── */
const EVENTS_WITH_DAYS = ['member_inactive_days', 'inactivity_seller_days', 'inactivity_buyer_days', 'no_followup_first_contact'];
const EVENTS_WITH_STAGE = ['pipeline_stage_reached'];

/* ── Acciones con params extra ───────────────────────────────── */
const ACTIONS_WITH_MESSAGE = ['email_admin', 'email_agent', 'email_all_members', 'notification_bell', 'notification_bell_all', 'org_group_message', 'direct_message_agent', 'create_alert'];
const ACTIONS_WITH_TASK = ['create_task'];

const defaultForm = () => ({
  name: '',
  event_type: 'seller_added',
  event_params: {},
  action_type: 'notification_bell',
  action_params: {},
});

/* ══════════════════════════════════════════════════════════════ */
export const OrgRules = ({ orgId, orgRole }) => {
  const { user } = useAuth();
  const canEdit = orgRole === 'owner' || orgRole === 'admin';

  /* ── state ─────────────────────────────────────────────────── */
  const [rules, setRules] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loadingRules, setLoadingRules] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(defaultForm());
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('rules'); // rules | settings | objectives

  // Settings — full rich state
  const [settingsForm, setSettingsForm] = useState({
    responsible_name: '',
    follow_up_cycle_days: 7,
    commission_default: 3.0,
    auto_assign: false,
    custom_labels: ['Nuevo', 'En proceso', 'Cerrado'],
    custom_priorities: ['Alta', 'Media', 'Baja'],
    pipeline_stages: [
      { name: 'Primer contacto', color: '#06b6d4' },
      { name: 'Visita realizada', color: '#c9a84c' },
      { name: 'Negociación', color: '#f59e0b' },
      { name: 'Oferta presentada', color: '#f97316' },
      { name: 'Firma pendiente', color: '#10b981' },
      { name: 'Cerrado', color: '#22c55e' },
    ],
    property_types: ['Piso', 'Casa', 'Chalet', 'Local comercial', 'Solar', 'Garaje', 'Trastero'],
    zones: [],
    budget_ranges: [
      { label: 'Hasta 100.000€', min: 0, max: 100000 },
      { label: '100.000 – 200.000€', min: 100000, max: 200000 },
      { label: '200.000 – 350.000€', min: 200000, max: 350000 },
      { label: '350.000 – 500.000€', min: 350000, max: 500000 },
      { label: '+500.000€', min: 500000, max: null },
    ],
    working_hours: { start: '09:00', end: '19:00', days: ['L','M','X','J','V'] },
    notification_prefs: {
      on_new_seller: true, on_new_buyer: true,
      on_close: true, daily_summary: false,
      weekly_summary: true, task_overdue: true,
    },
    followup_templates: [],
  });
  const [labelInput, setLabelInput] = useState('');
  const [priorityInput, setPriorityInput] = useState('');
  const [zoneInput, setZoneInput] = useState('');
  const [propTypeInput, setPropTypeInput] = useState('');
  const [stageInput, setStageInput] = useState('');
  const [stageColor, setStageColor] = useState('#c9a84c');
  const [templateInput, setTemplateInput] = useState({ name: '', message: '', days_after: 1 });
  const [savingSettings, setSavingSettings] = useState(false);

  // Objectives
  const [objectives, setObjectives] = useState({
    contacts: 50, sellers: 20, buyers: 20, closings: 10, commission_eur: 50000,
  });
  const [agentObjectives, setAgentObjectives] = useState({});
  const [savingObj, setSavingObj] = useState(false);
  const [members, setMembers] = useState([]);

  /* ── load ───────────────────────────────────────────────────── */
  useEffect(() => { load(); }, [orgId]);

  const load = async () => {
    setLoadingRules(true);
    try {
      const [r, s, m] = await Promise.all([
        axios.get(`${API}/organizations/${orgId}/rules`),
        axios.get(`${API}/organizations/${orgId}/settings`),
        axios.get(`${API}/organizations/${orgId}/members`),
      ]);
      setRules(r.data || []);
      const sd = s.data || {};
      setSettings(sd);
      setSettingsForm(prev => ({
        ...prev,
        responsible_name: sd.responsible_name || '',
        follow_up_cycle_days: sd.follow_up_cycle_days || 7,
        commission_default: sd.commission_default ?? 3.0,
        auto_assign: sd.auto_assign ?? false,
        custom_labels: sd.custom_labels || ['Nuevo', 'En proceso', 'Cerrado'],
        custom_priorities: sd.custom_priorities || ['Alta', 'Media', 'Baja'],
        pipeline_stages: sd.pipeline_stages || prev.pipeline_stages,
        property_types: sd.property_types || ['Piso', 'Casa', 'Chalet', 'Local comercial', 'Solar', 'Garaje', 'Trastero'],
        zones: sd.zones || [],
        budget_ranges: sd.budget_ranges || prev.budget_ranges,
        working_hours: sd.working_hours || prev.working_hours,
        notification_prefs: sd.notification_prefs || prev.notification_prefs,
        followup_templates: sd.followup_templates || [],
      }));
      if (sd.objectives) setObjectives({ ...objectives, ...sd.objectives });
      if (sd.agent_objectives) setAgentObjectives(sd.agent_objectives);
      setMembers((m.data?.members || []).filter(mem => mem.user_id !== user?.user_id || true));
    } catch (e) {
      console.error(e);
    }
    setLoadingRules(false);
  };

  /* ── rules CRUD ─────────────────────────────────────────────── */
  const handleCreateRule = async () => {
    if (!form.name.trim()) { toast.error('Ponle un nombre a la regla'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/organizations/${orgId}/rules`, form);
      toast.success('Regla creada');
      setShowModal(false);
      setForm(defaultForm());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear regla');
    }
    setSaving(false);
  };

  const handleToggle = async (rule) => {
    if (!canEdit) return;
    try {
      await axios.put(`${API}/organizations/${orgId}/rules/${rule.rule_id}`, { is_active: !rule.is_active });
      setRules(prev => prev.map(r => r.rule_id === rule.rule_id ? { ...r, is_active: !r.is_active } : r));
    } catch { toast.error('Error al actualizar regla'); }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('¿Eliminar esta regla?')) return;
    try {
      await axios.delete(`${API}/organizations/${orgId}/rules/${ruleId}`);
      setRules(prev => prev.filter(r => r.rule_id !== ruleId));
      toast.success('Regla eliminada');
    } catch { toast.error('Error al eliminar'); }
  };

  /* ── settings ───────────────────────────────────────────────── */
  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/organizations/${orgId}/settings`, {
        ...settingsForm,
        objectives,
        agent_objectives: agentObjectives,
      });
      toast.success('Configuración guardada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    }
    setSavingSettings(false);
  };

  /* ── objectives ─────────────────────────────────────────────── */
  const handleSaveObjectives = async () => {
    setSavingObj(true);
    try {
      await axios.put(`${API}/organizations/${orgId}/settings`, {
        ...settingsForm,
        objectives,
        agent_objectives: agentObjectives,
      });
      toast.success('Objetivos guardados');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar objetivos');
    }
    setSavingObj(false);
  };

  const setAgentObj = (userId, field, val) => {
    setAgentObjectives(prev => ({
      ...prev,
      [userId]: { ...(prev[userId] || {}), [field]: Number(val) },
    }));
  };

  /* ── Event/Action param renderers ───────────────────────────── */
  const renderEventParams = () => {
    if (EVENTS_WITH_DAYS.includes(form.event_type))
      return (
        <input type="number" min={1} placeholder="Número de días"
          value={form.event_params?.days || ''}
          onChange={e => setForm(p => ({ ...p, event_params: { days: parseInt(e.target.value) || 1 } }))}
          className="w-full bg-gray-800 border border-white/20 rounded-lg px-4 py-2 text-white text-sm mt-2" />
      );
    if (EVENTS_WITH_STAGE.includes(form.event_type))
      return (
        <input type="text" placeholder="Nombre de la etapa (ej: Negociación)"
          value={form.event_params?.stage || ''}
          onChange={e => setForm(p => ({ ...p, event_params: { stage: e.target.value } }))}
          className="w-full bg-gray-800 border border-white/20 rounded-lg px-4 py-2 text-white text-sm mt-2" />
      );
    return null;
  };

  const ENTITY_STATES = {
    seller:       ['nuevo','contactado','visita_agendada','exclusiva_firmada','cerrado','descartado'],
    buyer:        ['nuevo','contactado','visita_agendada','oferta_enviada','cerrado','descartado'],
    property:     ['captada','valorada','publicada','vendida'],
    professional: ['nuevo','contactado','colaboracion','cerrado'],
  };

  const updAP = (patch) => setForm(p => ({ ...p, action_params: { ...p.action_params, ...patch } }));

  const renderActionParams = () => {
    if (ACTIONS_WITH_TASK.includes(form.action_type)) {
      const ap = form.action_params || {};
      const entityStates = ENTITY_STATES[ap.entity_type] || [];
      const sel = 'w-full bg-gray-800 border border-white/20 rounded-lg px-3 py-2 text-white text-sm';
      const inp = 'w-full bg-gray-800 border border-white/20 rounded-lg px-3 py-2 text-white text-sm';
      return (
        <div className="space-y-3 mt-2">
          <div>
            <label className="text-xs text-white/50 mb-1 block">Título de la tarea *</label>
            <input type="text" placeholder="Ej: Contactar vendedor, Agendar visita..."
              value={ap.task_name || ''} onChange={e => updAP({ task_name: e.target.value })} className={inp} />
          </div>
          <div>
            <label className="text-xs text-white/50 mb-1 block">Instrucciones para el agente</label>
            <textarea rows={2} placeholder="Qué debe hacer el agente..."
              value={ap.description || ''} onChange={e => updAP({ description: e.target.value })}
              className="w-full bg-gray-800 border border-white/20 rounded-lg px-3 py-2 text-white text-sm resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-white/50 mb-1 block">Plazo (días desde el evento)</label>
              <input type="number" min="1" max="365" placeholder="1"
                value={ap.due_days || ''} onChange={e => updAP({ due_days: parseInt(e.target.value) || 1 })} className={inp} />
            </div>
            <div>
              <label className="text-xs text-white/50 mb-1 block">Prioridad</label>
              <select value={ap.priority || 'media'} onChange={e => updAP({ priority: e.target.value })} className={sel}>
                <option value="baja">Baja</option>
                <option value="media">Media</option>
                <option value="alta">Alta</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-white/50 mb-1 block">Figura del pipeline</label>
              <select value={ap.entity_type || ''} onChange={e => updAP({ entity_type: e.target.value, next_estado: '' })} className={sel}>
                <option value="">— Sin pipeline —</option>
                <option value="seller">Vendedor</option>
                <option value="buyer">Comprador</option>
                <option value="property">Propiedad captada</option>
                <option value="professional">Profesional</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-white/50 mb-1 block">Al completar → avanzar a estado</label>
              <select value={ap.next_estado || ''} onChange={e => updAP({ next_estado: e.target.value })}
                className={sel} disabled={!ap.entity_type}>
                <option value="">— No avanzar —</option>
                {entityStates.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        </div>
      );
    }
    if (ACTIONS_WITH_MESSAGE.includes(form.action_type))
      return (
        <textarea placeholder="Contenido del mensaje / notificación"
          value={form.action_params?.message || ''}
          onChange={e => setForm(p => ({ ...p, action_params: { message: e.target.value } }))}
          rows={2}
          className="w-full bg-gray-800 border border-white/20 rounded-lg px-4 py-2 text-white text-sm mt-2 resize-none" />
      );
    return null;
  };

  /* ── section nav ─────────────────────────────────────────────── */
  const sections = [
    { key: 'rules',      label: 'Reglas',       icon: <Zap size={14} /> },
    { key: 'settings',   label: 'Parámetros',   icon: <Settings size={14} /> },
    { key: 'objectives', label: 'Objetivos',     icon: <Target size={14} /> },
  ];

  /* ── render ─────────────────────────────────────────────────── */
  return (
    <div className="space-y-6">

      {/* Nav pills */}
      <div className="flex gap-2 flex-wrap">
        {sections.map(s => (
          <button key={s.key}
            onClick={() => setActiveSection(s.key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg font-bold text-sm transition border-2 ${
              activeSection === s.key
                ? 'border-yellow-500/60 text-black'
                : 'bg-gray-800 border-white/10 text-gray-300 hover:border-yellow-500/50'
            }`}
            style={activeSection === s.key ? { background: 'linear-gradient(135deg, #c9a84c, #e8c870)' } : {}}>
            {s.icon} {s.label}
          </button>
        ))}
      </div>

      {/* ══ SECTION: RULES ══════════════════════════════════════ */}
      {activeSection === 'rules' && (
        <div>
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-black text-white flex items-center gap-2">
              <Zap size={22} className="text-yellow-400" />
              Reglas y Automatizaciones
              <span className="ml-2 bg-amber-500/20 text-amber-300 text-xs px-2 py-0.5 rounded-full border border-amber-500/30">
                {rules.filter(r => r.is_active).length} activas
              </span>
            </h2>
            {canEdit && (
              <button
                onClick={() => { setForm(defaultForm()); setShowModal(true); }}
                className="text-black px-5 py-2.5 font-bold flex items-center gap-2 hover:scale-105 transition border-2 border-black shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] text-sm rounded-lg"
                style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c870)' }}
              >
                <Plus size={16} /> Nueva regla
              </button>
            )}
          </div>

          {/* Preloaded suggestions */}
          {canEdit && rules.length === 0 && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-5">
              <p className="text-amber-300 text-sm font-bold mb-2">💡 Sugerencias rápidas</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { name: 'Aviso nuevo vendedor', event_type: 'seller_added', action_type: 'notification_bell_all', event_params: {}, action_params: { message: 'Nuevo vendedor añadido al CRM' } },
                  { name: 'Email al cerrar operación', event_type: 'seller_completed', action_type: 'email_admin', event_params: {}, action_params: { message: 'Operación cerrada con éxito en el CRM' } },
                  { name: 'Alerta inactividad 7 días', event_type: 'inactivity_seller_days', action_type: 'notification_bell', event_params: { days: 7 }, action_params: { message: 'Vendedor sin actividad desde hace 7 días' } },
                  { name: 'Resumen semanal', event_type: 'weekly_summary', action_type: 'email_all_members', event_params: {}, action_params: { message: 'Resumen semanal de actividad del equipo' } },
                ].map((s, i) => (
                  <button key={i}
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/organizations/${orgId}/rules`, { ...s, is_active: true });
                        toast.success(`Regla "${s.name}" creada`);
                        load();
                      } catch { toast.error('Error'); }
                    }}
                    className="bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-200 text-xs px-3 py-1.5 rounded-full transition flex items-center gap-1">
                    <Plus size={10} /> {s.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {loadingRules ? (
            <p className="text-gray-400 text-center py-8">Cargando reglas...</p>
          ) : rules.length === 0 ? (
            <div className="bg-gray-900 border-2 border-dashed border-gray-700 rounded-xl p-10 text-center">
              <Zap size={40} className="mx-auto text-gray-600 mb-3" />
              <p className="text-gray-400">No hay reglas configuradas. Crea la primera.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {rules.map(rule => (
                <div key={rule.rule_id}
                  className={`flex items-start gap-3 p-4 rounded-xl border-2 transition-all ${
                    rule.is_active ? 'bg-gray-900 border-amber-500/40' : 'bg-gray-900/50 border-gray-700 opacity-55'
                  }`}>
                  <button onClick={() => handleToggle(rule)}
                    className={`flex-shrink-0 mt-0.5 transition ${canEdit ? 'cursor-pointer' : 'cursor-default'}`}>
                    {rule.is_active
                      ? <ToggleRight size={26} style={{ color: '#c9a84c' }} />
                      : <ToggleLeft size={26} className="text-gray-500" />}
                  </button>

                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold text-sm">{rule.name}</p>
                    <p className="text-gray-400 text-xs mt-0.5 leading-relaxed">
                      <span className="font-medium" style={{ color: '#c9a84c' }}>Cuando:</span>{' '}
                      {EVENT_LABEL[rule.event_type] || rule.event_type}
                      {rule.event_params?.days && <span className="text-white/60"> ({rule.event_params.days} días)</span>}
                      {rule.event_params?.stage && <span className="text-white/60"> "{rule.event_params.stage}"</span>}
                      <span className="font-medium mx-1.5" style={{ color: '#c9a84c' }}>→</span>
                      <span className="text-green-400 font-medium">Acción:</span>{' '}
                      {ACTION_LABEL[rule.action_type] || rule.action_type}
                      {rule.action_params?.message && <span className="text-white/60 italic"> "{rule.action_params.message.slice(0, 40)}{rule.action_params.message.length > 40 ? '…' : ''}"</span>}
                      {rule.action_params?.task_name && <span className="text-white/60 italic"> "{rule.action_params.task_name}"</span>}
                    </p>
                  </div>

                  {canEdit && (
                    <button onClick={() => handleDeleteRule(rule.rule_id)}
                      className="flex-shrink-0 p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/30 text-red-400 transition">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══ SECTION: SETTINGS ═══════════════════════════════════ */}
      {activeSection === 'settings' && canEdit && (
        <div className="space-y-5">
          <h2 className="text-xl font-black text-white flex items-center gap-2">
            <Settings size={20} className="text-gray-400" /> Parámetros de Organización
          </h2>

          {/* ── Generales ──────────────────────────────── */}
          <Section title="General" color="gold">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Responsable principal">
                <input type="text" placeholder="Nombre del responsable"
                  value={settingsForm.responsible_name}
                  onChange={e => setSettingsForm(p => ({ ...p, responsible_name: e.target.value }))}
                  className={inputCls} />
              </Field>
              <Field label="Ciclo de seguimiento por defecto (días)">
                <input type="number" min={1} max={365}
                  value={settingsForm.follow_up_cycle_days}
                  onChange={e => setSettingsForm(p => ({ ...p, follow_up_cycle_days: parseInt(e.target.value) || 7 }))}
                  className={inputCls} />
              </Field>
              <Field label="Comisión por defecto (%)">
                <input type="number" min={0} max={20} step={0.1}
                  value={settingsForm.commission_default}
                  onChange={e => setSettingsForm(p => ({ ...p, commission_default: parseFloat(e.target.value) || 0 }))}
                  className={inputCls} />
              </Field>
              <Field label="Asignación automática de contactos">
                <label className="flex items-center gap-3 mt-1 cursor-pointer">
                  <div onClick={() => setSettingsForm(p => ({ ...p, auto_assign: !p.auto_assign }))}
                    className="w-12 h-6 rounded-full transition-colors relative"
                    style={{ backgroundColor: settingsForm.auto_assign ? '#c9a84c' : '#4b5563' }}>
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${settingsForm.auto_assign ? 'translate-x-7' : 'translate-x-1'}`} />
                  </div>
                  <span className="text-gray-300 text-sm">{settingsForm.auto_assign ? 'Activada' : 'Desactivada'}</span>
                </label>
              </Field>
            </div>
          </Section>

          {/* ── Horario laboral ────────────────────────── */}
          <Section title="Horario de trabajo" color="blue">
            <div className="grid md:grid-cols-3 gap-4">
              <Field label="Hora de inicio">
                <input type="time" value={settingsForm.working_hours?.start || '09:00'}
                  onChange={e => setSettingsForm(p => ({ ...p, working_hours: { ...p.working_hours, start: e.target.value } }))}
                  className={inputCls} />
              </Field>
              <Field label="Hora de fin">
                <input type="time" value={settingsForm.working_hours?.end || '19:00'}
                  onChange={e => setSettingsForm(p => ({ ...p, working_hours: { ...p.working_hours, end: e.target.value } }))}
                  className={inputCls} />
              </Field>
              <Field label="Días laborables">
                <div className="flex gap-1 flex-wrap mt-1">
                  {['L','M','X','J','V','S','D'].map(d => {
                    const days = settingsForm.working_hours?.days || [];
                    const active = days.includes(d);
                    return (
                      <button key={d} type="button"
                        onClick={() => setSettingsForm(p => ({
                          ...p,
                          working_hours: { ...p.working_hours, days: active ? days.filter(x => x !== d) : [...days, d] }
                        }))}
                        className={`w-8 h-8 rounded font-bold text-xs transition ${active ? 'bg-blue-500 text-white' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'}`}>
                        {d}
                      </button>
                    );
                  })}
                </div>
              </Field>
            </div>
          </Section>

          {/* ── Etapas del pipeline ────────────────────── */}
          <Section title="Etapas del pipeline" color="teal">
            <div className="space-y-2 mb-3">
              {settingsForm.pipeline_stages.map((stage, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color }} />
                  <span className="text-white text-sm flex-1">{stage.name}</span>
                  <button onClick={() => setSettingsForm(p => ({ ...p, pipeline_stages: p.pipeline_stages.filter((_, j) => j !== i) }))}
                    className="text-gray-500 hover:text-red-400 transition"><X size={13} /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 items-center">
              <input type="color" value={stageColor} onChange={e => setStageColor(e.target.value)}
                className="w-9 h-9 rounded cursor-pointer bg-transparent border-0" />
              <input type="text" placeholder="Nombre de la etapa" value={stageInput}
                onChange={e => setStageInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && stageInput.trim()) { setSettingsForm(p => ({ ...p, pipeline_stages: [...p.pipeline_stages, { name: stageInput.trim(), color: stageColor }] })); setStageInput(''); } }}
                className="flex-1 bg-gray-800 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500" />
              <button onClick={() => { if (stageInput.trim()) { setSettingsForm(p => ({ ...p, pipeline_stages: [...p.pipeline_stages, { name: stageInput.trim(), color: stageColor }] })); setStageInput(''); } }}
                className="bg-teal-600 text-white px-3 py-2 rounded-lg text-sm font-bold hover:bg-teal-500 transition">+</button>
            </div>
          </Section>

          {/* ── Tipos de propiedad ─────────────────────── */}
          <Section title="Tipos de propiedad" color="orange">
            <div className="flex flex-wrap gap-2 mb-3">
              {settingsForm.property_types.map(pt => (
                <span key={pt} className="bg-orange-500/20 text-orange-300 text-xs px-3 py-1.5 rounded-full flex items-center gap-1 border border-orange-500/30">
                  {pt}
                  <button onClick={() => setSettingsForm(p => ({ ...p, property_types: p.property_types.filter(x => x !== pt) }))}
                    className="hover:text-red-400 transition"><X size={10} /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input type="text" placeholder="Nuevo tipo" value={propTypeInput}
                onChange={e => setPropTypeInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { const v = propTypeInput.trim(); if (v && !settingsForm.property_types.includes(v)) { setSettingsForm(p => ({ ...p, property_types: [...p.property_types, v] })); setPropTypeInput(''); } } }}
                className="flex-1 bg-gray-800 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500" />
              <button onClick={() => { const v = propTypeInput.trim(); if (v && !settingsForm.property_types.includes(v)) { setSettingsForm(p => ({ ...p, property_types: [...p.property_types, v] })); setPropTypeInput(''); } }}
                className="bg-orange-500 text-white px-3 py-2 rounded-lg text-sm font-bold hover:bg-orange-400 transition">+</button>
            </div>
          </Section>

          {/* ── Zonas de trabajo ───────────────────────── */}
          <Section title="Zonas de trabajo" color="green">
            <div className="flex flex-wrap gap-2 mb-3">
              {(settingsForm.zones || []).length === 0 && (
                <p className="text-gray-500 text-sm">Sin zonas configuradas</p>
              )}
              {(settingsForm.zones || []).map(z => (
                <span key={z} className="bg-green-500/20 text-green-300 text-xs px-3 py-1.5 rounded-full flex items-center gap-1 border border-green-500/30">
                  {z}
                  <button onClick={() => setSettingsForm(p => ({ ...p, zones: p.zones.filter(x => x !== z) }))}
                    className="hover:text-red-400 transition"><X size={10} /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input type="text" placeholder="Ej: Centro Madrid, Zona Norte..." value={zoneInput}
                onChange={e => setZoneInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { const v = zoneInput.trim(); if (v) { setSettingsForm(p => ({ ...p, zones: [...(p.zones || []), v] })); setZoneInput(''); } } }}
                className="flex-1 bg-gray-800 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-green-500" />
              <button onClick={() => { const v = zoneInput.trim(); if (v) { setSettingsForm(p => ({ ...p, zones: [...(p.zones || []), v] })); setZoneInput(''); } }}
                className="bg-green-600 text-white px-3 py-2 rounded-lg text-sm font-bold hover:bg-green-500 transition">+</button>
            </div>
          </Section>

          {/* ── Etiquetas y prioridades ────────────────── */}
          <Section title="Etiquetas y prioridades" color="gold">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1"><Tag size={11} /> Etiquetas</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {settingsForm.custom_labels.map(l => (
                    <span key={l} className="bg-amber-500/20 text-amber-300 text-xs px-2.5 py-1 rounded-full flex items-center gap-1 border border-amber-500/30">
                      {l}<button onClick={() => setSettingsForm(p => ({ ...p, custom_labels: p.custom_labels.filter(x => x !== l) }))} className="hover:text-red-400"><X size={9} /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input type="text" placeholder="Nueva etiqueta" value={labelInput} onChange={e => setLabelInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { const v = labelInput.trim(); if (v && !settingsForm.custom_labels.includes(v)) { setSettingsForm(p => ({ ...p, custom_labels: [...p.custom_labels, v] })); setLabelInput(''); } } }}
                    className="flex-1 bg-gray-800 border border-white/20 rounded px-3 py-1.5 text-white text-sm focus:outline-none focus:border-yellow-500" />
                  <button onClick={() => { const v = labelInput.trim(); if (v && !settingsForm.custom_labels.includes(v)) { setSettingsForm(p => ({ ...p, custom_labels: [...p.custom_labels, v] })); setLabelInput(''); } }}
                    className="text-black px-3 py-1.5 rounded text-sm font-bold"
                    style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c870)' }}>+</button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">Prioridades</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {settingsForm.custom_priorities.map(pr => (
                    <span key={pr} className="bg-orange-500/20 text-orange-300 text-xs px-2.5 py-1 rounded-full flex items-center gap-1 border border-orange-500/30">
                      {pr}<button onClick={() => setSettingsForm(p => ({ ...p, custom_priorities: p.custom_priorities.filter(x => x !== pr) }))} className="hover:text-red-400"><X size={9} /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input type="text" placeholder="Nueva prioridad" value={priorityInput} onChange={e => setPriorityInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { const v = priorityInput.trim(); if (v && !settingsForm.custom_priorities.includes(v)) { setSettingsForm(p => ({ ...p, custom_priorities: [...p.custom_priorities, v] })); setPriorityInput(''); } } }}
                    className="flex-1 bg-gray-800 border border-white/20 rounded px-3 py-1.5 text-white text-sm focus:outline-none focus:border-orange-500" />
                  <button onClick={() => { const v = priorityInput.trim(); if (v && !settingsForm.custom_priorities.includes(v)) { setSettingsForm(p => ({ ...p, custom_priorities: [...p.custom_priorities, v] })); setPriorityInput(''); } }}
                    className="bg-orange-500 text-white px-3 py-1.5 rounded text-sm font-bold">+</button>
                </div>
              </div>
            </div>
          </Section>

          {/* ── Notificaciones ─────────────────────────── */}
          <Section title="Preferencias de notificación" color="yellow">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { key: 'on_new_seller', label: 'Nuevo vendedor' },
                { key: 'on_new_buyer', label: 'Nuevo comprador' },
                { key: 'on_close', label: 'Operación cerrada' },
                { key: 'task_overdue', label: 'Tarea vencida' },
                { key: 'daily_summary', label: 'Resumen diario' },
                { key: 'weekly_summary', label: 'Resumen semanal' },
              ].map(n => {
                const active = settingsForm.notification_prefs?.[n.key];
                return (
                  <label key={n.key} className="flex items-center gap-2 cursor-pointer">
                    <div onClick={() => setSettingsForm(p => ({ ...p, notification_prefs: { ...(p.notification_prefs || {}), [n.key]: !active } }))}
                      className="w-10 h-5 rounded-full transition-colors relative flex-shrink-0"
                      style={{ backgroundColor: active ? '#c9a84c' : '#4b5563' }}>
                      <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${active ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </div>
                    <span className="text-gray-300 text-xs">{n.label}</span>
                  </label>
                );
              })}
            </div>
          </Section>

          {/* ── Plantillas de seguimiento ──────────────── */}
          <Section title="Plantillas de seguimiento" color="gold">
            <div className="space-y-2 mb-4">
              {(settingsForm.followup_templates || []).length === 0 && (
                <p className="text-gray-500 text-sm">Sin plantillas. Añade mensajes predefinidos para el seguimiento.</p>
              )}
              {(settingsForm.followup_templates || []).map((t, i) => (
                <div key={i} className="bg-gray-800 rounded-lg p-3 flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <p className="text-white text-sm font-semibold">{t.name} <span className="text-gray-400 font-normal text-xs">(a los {t.days_after} días)</span></p>
                    <p className="text-gray-400 text-xs mt-0.5 truncate">{t.message}</p>
                  </div>
                  <button onClick={() => setSettingsForm(p => ({ ...p, followup_templates: p.followup_templates.filter((_, j) => j !== i) }))}
                    className="text-gray-500 hover:text-red-400 transition"><Trash2 size={13} /></button>
                </div>
              ))}
            </div>
            <div className="space-y-2 bg-gray-800/50 rounded-lg p-3">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">Nueva plantilla</p>
              <div className="grid md:grid-cols-3 gap-2">
                <input type="text" placeholder="Nombre (ej: Bienvenida)"
                  value={templateInput.name}
                  onChange={e => setTemplateInput(p => ({ ...p, name: e.target.value }))}
                  className="bg-gray-800 border border-white/20 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-yellow-500" />
                <div className="flex items-center gap-2">
                  <input type="number" min={0} placeholder="Días"
                    value={templateInput.days_after}
                    onChange={e => setTemplateInput(p => ({ ...p, days_after: parseInt(e.target.value) || 0 }))}
                    className="flex-1 bg-gray-800 border border-white/20 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-yellow-500" />
                  <span className="text-gray-400 text-sm whitespace-nowrap">días</span>
                </div>
                <button
                  onClick={() => {
                    if (!templateInput.name.trim() || !templateInput.message.trim()) return;
                    setSettingsForm(p => ({ ...p, followup_templates: [...(p.followup_templates || []), { ...templateInput }] }));
                    setTemplateInput({ name: '', message: '', days_after: 1 });
                  }}
                  className="text-black px-3 py-2 rounded text-sm font-bold transition"
                  style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c870)' }}>
                  Añadir
                </button>
              </div>
              <textarea placeholder="Mensaje de la plantilla... usa {nombre} para el nombre del contacto"
                value={templateInput.message}
                onChange={e => setTemplateInput(p => ({ ...p, message: e.target.value }))}
                rows={2}
                className="w-full bg-gray-800 border border-white/20 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-yellow-500 resize-none" />
            </div>
          </Section>

          <button onClick={handleSaveSettings} disabled={savingSettings}
            className="w-full text-black py-3.5 font-bold rounded-xl hover:opacity-90 transition disabled:opacity-50 text-sm"
            style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c870)' }}>
            {savingSettings ? 'Guardando...' : '💾 Guardar toda la configuración'}
          </button>
        </div>
      )}

      {/* ══ SECTION: OBJECTIVES ═════════════════════════════════ */}
      {activeSection === 'objectives' && (
        <div className="space-y-6">
          <h2 className="text-xl font-black text-white flex items-center gap-2">
            <Target size={20} className="text-teal-400" /> Gestión de Objetivos
          </h2>

          {/* Objetivos globales */}
          <div className="bg-gray-900 border-2 border-teal-500/30 rounded-xl p-5">
            <h3 className="text-white font-bold mb-4 flex items-center gap-2">
              <TrendingUp size={16} className="text-teal-400" /> Objetivos globales de la organización
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { key: 'contacts',       label: 'Total contactos',    unit: '' },
                { key: 'sellers',        label: 'Vendedores captados', unit: '' },
                { key: 'buyers',         label: 'Compradores activos', unit: '' },
                { key: 'closings',       label: 'Operaciones cerradas',unit: '' },
                { key: 'commission_eur', label: 'Comisiones (€)',     unit: '€' },
              ].map(f => (
                <div key={f.key} className="bg-gray-800 rounded-lg p-3">
                  <label className="block text-xs text-gray-400 mb-1.5">{f.label}</label>
                  <div className="flex items-center gap-2">
                    {f.unit && <span className="text-gray-400 text-sm">{f.unit}</span>}
                    <input type="number" min={0}
                      value={objectives[f.key] || 0}
                      onChange={e => setObjectives(p => ({ ...p, [f.key]: Number(e.target.value) }))}
                      disabled={!canEdit}
                      className="w-full bg-gray-700 border border-white/20 rounded px-3 py-1.5 text-white text-sm focus:outline-none focus:border-teal-500 disabled:opacity-50" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Objetivos por agente */}
          {members.length > 0 && (
            <div className="bg-gray-900 border-2 border-amber-500/30 rounded-xl p-5">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                <Users size={16} style={{ color: '#c9a84c' }} /> Objetivos individuales por agente
              </h3>
              <div className="space-y-4">
                {members.map(mem => {
                  const aObj = agentObjectives[mem.user_id] || {};
                  return (
                    <div key={mem.user_id} className="bg-gray-800 rounded-xl p-4">
                      <div className="flex items-center gap-3 mb-3">
                        {mem.picture ? (
                          <img src={mem.picture} alt={mem.name} className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-black text-xs font-bold"
                            style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c870)' }}>
                            {(mem.name || 'A')[0].toUpperCase()}
                          </div>
                        )}
                        <div>
                          <p className="text-white font-semibold text-sm">{mem.name || mem.email}</p>
                          <p className="text-gray-400 text-xs capitalize">{mem.role}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                          { key: 'contacts', label: 'Contactos' },
                          { key: 'sellers',  label: 'Vendedores' },
                          { key: 'buyers',   label: 'Compradores' },
                          { key: 'closings', label: 'Cierres' },
                        ].map(f => (
                          <div key={f.key}>
                            <label className="block text-xs text-gray-400 mb-1">{f.label}</label>
                            <input type="number" min={0}
                              value={aObj[f.key] || 0}
                              onChange={e => setAgentObj(mem.user_id, f.key, e.target.value)}
                              disabled={!canEdit}
                              className="w-full bg-gray-700 border border-white/20 rounded px-3 py-1.5 text-white text-sm focus:outline-none focus:border-yellow-500 disabled:opacity-50" />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {canEdit && (
            <button onClick={handleSaveObjectives} disabled={savingObj}
              className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-3 font-bold rounded-xl hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2">
              <Save size={16} />
              {savingObj ? 'Guardando objetivos...' : 'Guardar objetivos'}
            </button>
          )}
        </div>
      )}

      {/* ══ MODAL NUEVA REGLA ═══════════════════════════════════ */}
      {showModal && (
        <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-2xl p-6 w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto"
            style={{ border: '1px solid rgba(201,168,76,0.4)' }}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Zap size={18} className="text-yellow-400" /> Nueva regla de automatización
              </h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-5">
              {/* Nombre */}
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">Nombre de la regla *</label>
                <input type="text" placeholder="Ej: Avisar cuando hay nuevo vendedor"
                  value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  className={inputCls} />
              </div>

              {/* Evento */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: '#c9a84c' }}>
                  ⚡ CUANDO ocurra — Evento
                </label>
                <select value={form.event_type}
                  onChange={e => setForm(p => ({ ...p, event_type: e.target.value, event_params: {} }))}
                  className={inputCls}>
                  {EVENT_GROUPS.map(g => (
                    <optgroup key={g.group} label={`${g.icon} ${g.group}`}>
                      {g.events.map(ev => <option key={ev.value} value={ev.value}>{ev.label}</option>)}
                    </optgroup>
                  ))}
                </select>
                {renderEventParams()}
              </div>

              {/* Acción */}
              <div>
                <label className="block text-xs font-bold text-green-400 uppercase tracking-wide mb-1.5">
                  ➜ ENTONCES hacer — Acción
                </label>
                <select value={form.action_type}
                  onChange={e => setForm(p => ({ ...p, action_type: e.target.value, action_params: {} }))}
                  className={inputCls}>
                  {ACTION_GROUPS.map(g => (
                    <optgroup key={g.group} label={`${g.icon} ${g.group}`}>
                      {g.actions.map(ac => <option key={ac.value} value={ac.value}>{ac.label}</option>)}
                    </optgroup>
                  ))}
                </select>
                {renderActionParams()}
              </div>

              {/* Preview */}
              {form.name && (
                <div className="rounded-xl p-4 text-sm" style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)' }}>
                  <p className="font-bold mb-1 text-xs uppercase tracking-wide" style={{ color: '#c9a84c' }}>Vista previa</p>
                  <p className="text-gray-200">
                    <span className="font-bold" style={{ color: '#c9a84c' }}>"{form.name}"</span>{' — '}
                    Cuando <span className="text-yellow-300">{EVENT_LABEL[form.event_type]}</span>
                    {' → '} <span className="text-green-300">{ACTION_LABEL[form.action_type]}</span>
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={handleCreateRule} disabled={saving}
                className="flex-1 text-black py-3 font-bold rounded-xl hover:opacity-90 transition disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c870)' }}>
                {saving ? 'Creando...' : 'Crear regla'}
              </button>
              <button onClick={() => setShowModal(false)}
                className="flex-1 bg-gray-700 text-white py-3 font-bold rounded-xl hover:bg-gray-600 transition">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
